import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";
import { fetchMinistries } from "routes";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  churchName: PropTypes.string,
  ministryName: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

// Define individual Cell components with PropTypes
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value}
  </MDTypography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

// Create a factory function for ChurchCell that receives the church object
const createChurchCell = (church) => {
  const ChurchCell = ({ value }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar name={value} size="sm" />
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {value}
        </MDTypography>
        <MDTypography variant="caption">{church?.email || "church@example.com"}</MDTypography>
      </MDBox>
    </MDBox>
  );
  ChurchCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  return ChurchCell;
};

const MinistryCell = ({ value, row }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="button" fontWeight="medium">
      {value}
    </MDTypography>
    <MDTypography variant="caption" color="text">
      Created: {new Date(row.original.createdAt).toLocaleDateString()}
    </MDTypography>
  </MDBox>
);
MinistryCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const StatusCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "active" ? "success" : "error"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <MDBox display="flex" justifyContent="center" gap={1}>
    <MDButton variant="gradient" color="info" size="small" onClick={() => handleEdit(row.original)}>
      <Icon>edit</Icon>&nbsp;Edit
    </MDButton>
    <MDButton
      variant="gradient"
      color="error"
      size="small"
      onClick={() => handleDelete(row.original)}
    >
      <Icon>delete</Icon>&nbsp;Delete
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useMinistryData() {
  const { user, church } = useAuth();

  // Create ChurchCell with access to the church object
  const ChurchCell = createChurchCell(church);

  const [ministryData, setMinistryData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "Church",
        accessor: "churchName",
        align: "left",
        width: "30%",
        Cell: ChurchCell,
      },
      {
        Header: "Ministry",
        accessor: "ministryName",
        align: "left",
        width: "25%",
        Cell: MinistryCell,
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "15%",
        Cell: StatusCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState(null);

  const handleEdit = (ministry) => {
    setSelectedMinistry(ministry);
    setEditModalOpen(true);
  };

  const handleDelete = (ministry) => {
    setSelectedMinistry(ministry);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedMinistry.id
        ? `https://api.ofi-cia.com/api/ministries/${selectedMinistry.id}`
        : "https://api.ofi-cia.com/api/ministries";

      const method = selectedMinistry.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: selectedMinistry.ministryName,
          status: selectedMinistry.status === "active",
          church_id: church.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      Swal.fire({
        title: "Success!",
        text: `Ministry successfully saved`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchMinistries();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Ministry operation failed:", error);
      setEditModalOpen(false);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      //   setMinistryData((prev) => ({
      //     ...prev,
      //     error: error.message,
      //   }));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(
        `https://api.ofi-cia.com/api/ministries/${selectedMinistry.id}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Ministry, ${selectedMinistry.ministryName} deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchMinistries();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      setMinistryData((prev) => ({
        ...prev,
        error: error.message,
      }));
    }
  };

  const handleAddMinistry = () => {
    setSelectedMinistry({
      id: null,
      ministryName: "",
      status: "active",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchMinistries = async () => {
    try {
      setMinistryData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`https://api.ofi-cia.com/api/ministries/church/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const ministries = result.data || result.ministries || result;

      if (!Array.isArray(ministries)) {
        throw new Error("API response is not an array");
      }

      setMinistryData((prev) => ({
        ...prev,
        rows: ministries.map((min, index) => ({
          id: min.id,
          serial: index + 1,
          churchName: church.name,
          ministryName: min.name || min.ministry_name,
          status: min.status ? "active" : "inactive",
          createdAt: min.created_at,
          originalData: min,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setMinistryData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchMinistries();
  }, [user, church]);

  return {
    ...ministryData,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMinistry,
    setSelectedMinistry,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMinistry,
  };
}
